import { useCallback, useLayoutEffect, useMemo } from 'react'
import { onSnapshot, doc } from 'firebase/firestore'
import { db } from '../../../../firebase'
import { RUNDOWN_COLLECTION } from '@rundown-studio/consts'
import type { DocumentSnapshot } from '@firebase/firestore'
import { useQueryClient } from '@tanstack/react-query'
import { variablesQueryKeys } from '../../../variables/api/hooks'
import { parseRundownSnapshot } from '../../../../utils/parseSnapshotWrappers'
import type { Rundown } from '@rundown-studio/types'
import type { TextVariableDto } from '../../../variables/model/dtos'
import { useAtom } from 'jotai'
import { variablesAtom } from '../../../variables'

type UseRundownListenerParams = {
  rundownId: string
  callback?: (snapshot: DocumentSnapshot) => void
}

export const useRundownListener = ({
  rundownId,
  callback,
}: UseRundownListenerParams) => {
  const queryClient = useQueryClient()
  const [variables, setVariables] = useAtom(variablesAtom)

  const variablesListKey = useMemo(
    () => variablesQueryKeys.list({ rundownId }).queryKey,
    [rundownId],
  )

  const handleUpdate = useCallback(
    (snapshot: DocumentSnapshot) => {
      if (!snapshot.exists()) {
        // Remove all variable queries and reset state when document is gone
        Object.keys(variables ?? {}).forEach((key) => {
          const queryKey = variablesQueryKeys.byVariableKey({
            rundownId,
            variableKey: key,
          }).queryKey
          queryClient.removeQueries({ queryKey })
        })
        void queryClient.removeQueries({ queryKey: variablesListKey })
        setVariables({})
        callback?.(snapshot)
        return
      }

      const rundownData = parseRundownSnapshot(snapshot) as Rundown
      const { textVariables } = rundownData
      const rundownVariablesKeys = Object.keys(textVariables)

      queryClient.setQueryData(variablesListKey, textVariables)

      Object.keys(variables ?? {}).forEach((key) => {
        if (!rundownVariablesKeys.includes(key)) {
          queryClient.removeQueries({
            queryKey: variablesQueryKeys.byVariableKey({
              rundownId,
              variableKey: key,
            }).queryKey,
          })
        }
      })

      for (const key of rundownVariablesKeys) {
        queryClient.setQueryData(
          variablesQueryKeys.byVariableKey({ rundownId, variableKey: key })
            .queryKey,
          {
            key,
            value: textVariables[key],
          } satisfies TextVariableDto,
        )
      }

      setVariables(textVariables)
      callback?.(snapshot)
    },
    // No need to depend on variables and setVariables. Those are state updates causing max update depth exceeded error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callback, queryClient, rundownId, variablesListKey],
  )

  useLayoutEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, RUNDOWN_COLLECTION, rundownId),
      handleUpdate,
    )
    return () => unsubscribe()
  }, [rundownId, handleUpdate])
}
